import { isAuthenticatedViaMiddlewareCookie } from '@/lib/isAuthenticatedViaMiddlewareCookie'
import { onDatadogRumReady } from '@/lib/analytics/datadogRum'

const SEGMENT_ANONYMOUS_ID_KEY = 'ajs_anonymous_id'
const NILE_LD_KEY = 'nile_ld_key'

/**
 * Segment cookie values are sometimes JSON-encoded strings with quotes.
 * Mirrors unwrap logic in lib/launchDarklyServer.ts.
 */
export function unwrapSegmentCookieValue(raw: string | null | undefined): string | undefined {
    if (!raw || raw === 'null' || raw === '""') {
        return undefined
    }
    const trimmed = decodeURIComponent(raw).replace(/^"|"$/g, '')
    return trimmed || undefined
}

function readCookie(name: string): string | undefined {
    if (typeof document === 'undefined') {
        return undefined
    }
    const prefix = `${name}=`
    const entry = document.cookie.split('; ').find((row) => row.startsWith(prefix))
    if (!entry) {
        return undefined
    }
    return unwrapSegmentCookieValue(entry.slice(prefix.length))
}

/**
 * Returns the anonymous id Segment uses before identify(), in priority order:
 * ajs_anonymous_id cookie, ajs_anonymous_id localStorage, then nile_ld_key cookie.
 */
export function resolveAnonymousTrackingId(): string | undefined {
    const fromCookie = readCookie(SEGMENT_ANONYMOUS_ID_KEY)
    if (fromCookie) {
        return fromCookie
    }

    if (typeof window !== 'undefined') {
        try {
            const fromLocalStorage = unwrapSegmentCookieValue(localStorage.getItem(SEGMENT_ANONYMOUS_ID_KEY))
            if (fromLocalStorage) {
                return fromLocalStorage
            }
        } catch {
            // localStorage may be unavailable in restricted browser contexts
        }
    }

    return readCookie(NILE_LD_KEY)
}

export type DatadogRumUser = {
    id: string
    signupSkuId?: string
}

/** Attach a user id to the current Datadog RUM session (id only — no PII fields). */
export function setDatadogRumUser(user: DatadogRumUser): void {
    onDatadogRumReady((datadogRum) => {
        datadogRum.setUser({
            id: user.id,
            ...(user.signupSkuId && { signupSkuId: user.signupSkuId }),
        })
    })
}

/**
 * Tag guest RUM sessions with the same anonymous id Segment sends on track()
 * events so PostSessionReplayNotificationJob Datadog links resolve.
 *
 * Skips when middleware reports an authenticated session; UserContext sets the
 * patient id instead.
 */
export function initializeDatadogAnonymousUser(): void {
    if (typeof window === 'undefined') {
        return
    }

    if (isAuthenticatedViaMiddlewareCookie()) {
        return
    }

    const anonymousId = resolveAnonymousTrackingId()
    if (!anonymousId) {
        return
    }

    setDatadogRumUser({ id: anonymousId })
}
